import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  FileText,
  Building,
  Briefcase,
  Megaphone,
  Bell,
  Box as BoxIcon,
  BarChart2,
  UserCheck,
  Clock,
  FileCheck,
  Layers,
  UserPlus,
  Presentation,
  BookMarked,
  CalendarClock,
  CalendarDays,
  ClipboardEdit,
  Award,
  Flag,
  Wallet,
  PieChart,
  CircleDollarSign,
  Landmark,
  Banknote,
  MessageSquareText,
  Send,
  Truck,
  ShoppingCart,
  UserCog,
  ShieldCheck,
  History,
  Newspaper,
  Ungroup,
  LucideIcon,
  Coins,
} from "lucide-react";

// Strongly typed permissions based on your entities
export const PERMISSIONS = {
  // Academics
  CLASSROOM_READ: "classroom:read_all",
  SUBJECT_READ: "subject:read_all",
  TEACHER_SUBJECT_READ: "teacher_subject:read_all",
  TIMESLOT_READ: "timeslot:read_all",
  CURRICULUM_REQ_READ: "curriculum_requirement:read_all",
  TIMETABLE_SLOT_READ: "timetable_slot:read_all",
  TIMETABLE_READ: "timetable:read_all",
  ASSESSMENT_READ: "assessment:read_all",
  ATTENDANCE_READ: "attendance_record:read_all",
  ACADEMIC_YEAR_READ: "academic_year:read_all",
  TERM_READ: "term:read_all",
  GRADE_LEVEL_READ: "grade_level:read_all",
  TERM_REPORT_READ: "student_term_report:read_all",
  TRANSITION_READ: "transition_record:read_all",

  // People
  STUDENT_READ: "student:read_all",
  TEACHER_READ: "teacher:read_all",
  PARENT_READ: "parent:read_all",
  ENROLLMENT_READ: "enrollment:read_all",

  // Finance
  VOTE_HEAD_READ: "vote_head:read_all",
  FEE_STRUCTURE_READ: "fee_structure:read_all",
  FEE_ALLOCATION_READ: "student_fee_allocation:read_all",
  BANK_STAGING_READ: "bank_staging:read_all",
  STUDENT_LEDGER_READ: "student_fee_ledger:read_all",
  SUPPLIER_INVOICE_READ: "supplier_invoice:read_all",
  SUPPLIER_PAYMENT_READ: "supplier_payment:read_all",
  PAYMENT_READ: "payment:read_all",

  // Comms
  ANNOUNCEMENT_READ: "announcement:read_all",
  NOTIFICATION_READ: "notification:read_all",
  SMS_TEMPLATE_READ: "sms_template:read_all",
  SMS_OUTBOX_READ: "sms_outbox:read_all",
  NEWSLETTER_READ: "newsletter:read_all",

  // Inventory
  INVENTORY_READ: "inventory_item:read_all",
  STOCK_READ: "stock_level:read_all",
  INVENTORY_LOG_READ: "inventory_log:read_all",

  // Procurement
  SUPPLIER_READ: "supplier:read_all",
  PO_READ: "purchase_order:read_all",

  // Admin
  BRANCH_READ: "branch:read_all",
  USER_READ: "user:read_all",
  ROLE_READ: "role:read_all",
  AUDIT_LOG_READ: "audit_log:read_all",
} as const;

export interface SidebarItem {
  title: string;
  path: string;
  icon: LucideIcon;
  permission: string | null;
  componentKey: string;
}

export interface SidebarHeader {
  isHeader: true;
  title: string;
}

export type SidebarEntry = SidebarItem | SidebarHeader;

const createItem = (
  title: string,
  path: string,
  icon: LucideIcon,
  permission: string | null,
  componentKey: string,
): SidebarItem => ({
  title,
  path,
  icon,
  permission,
  componentKey,
});

const createHeader = (title: string): SidebarHeader => ({
  isHeader: true,
  title,
});

export const SIDEBAR_ITEMS: SidebarEntry[] = [
  createItem("Dashboard", "/dashboard", LayoutDashboard, null, "DASHBOARD"),

  // --- ACADEMICS ---
  createHeader("Academics"),
  createItem(
    "Classrooms",
    "/classrooms",
    Presentation,
    PERMISSIONS.CLASSROOM_READ,
    "CLASSROOMS",
  ),
  createItem(
    "Subjects",
    "/subjects",
    BookOpen,
    PERMISSIONS.SUBJECT_READ,
    "SUBJECTS",
  ),
  createItem(
    "Teacher Subjects",
    "/teacher-subjects",
    BookMarked,
    PERMISSIONS.TEACHER_SUBJECT_READ,
    "TEACHER_SUBJECTS",
  ),
  createItem(
    "Timeslots",
    "/timeslots",
    Clock,
    PERMISSIONS.TIMESLOT_READ,
    "TIMESLOTS",
  ),
  createItem(
    "Curriculum Reqs",
    "/curriculum-requirements",
    FileCheck,
    PERMISSIONS.CURRICULUM_REQ_READ,
    "CURRICULUM_REQUIREMENTS",
  ),
  createItem(
    "Timetable Slots",
    "/timetable-slots",
    CalendarClock,
    PERMISSIONS.TIMETABLE_SLOT_READ,
    "TIMETABLE_SLOTS",
  ),
  createItem(
    "Timetables",
    "/timetables",
    CalendarDays,
    PERMISSIONS.TIMETABLE_READ,
    "TIMETABLES",
  ),
  createItem(
    "Assessments",
    "/assessments",
    ClipboardEdit,
    PERMISSIONS.ASSESSMENT_READ,
    "ASSESSMENTS",
  ),
  createItem(
    "Attendance",
    "/attendance-records",
    UserCheck,
    PERMISSIONS.ATTENDANCE_READ,
    "ATTENDANCE_RECORDS",
  ),
  createItem(
    "Academic Years",
    "/academic-years",
    Calendar,
    PERMISSIONS.ACADEMIC_YEAR_READ,
    "ACADEMIC_YEARS",
  ),
  createItem("Terms", "/terms", Flag, PERMISSIONS.TERM_READ, "TERMS"),
  createItem(
    "Grade Levels",
    "/grade-levels",
    Layers,
    PERMISSIONS.GRADE_LEVEL_READ,
    "GRADE_LEVELS",
  ),
  createItem(
    "Students Reports",
    "/students-term-reports",
    Award,
    PERMISSIONS.TERM_REPORT_READ,
    "STUDENT_TERM_REPORTS",
  ),
  createItem(
    "Transition Records",
    "/transition-records",
    Ungroup,
    PERMISSIONS.TRANSITION_READ,
    "TRANSITION_RECORDS",
  ),

  // --- PEOPLE ---
  createHeader("People"),
  createItem(
    "Students",
    "/students",
    GraduationCap,
    PERMISSIONS.STUDENT_READ,
    "STUDENTS",
  ),
  createItem(
    "Teachers",
    "/teachers",
    Briefcase,
    PERMISSIONS.TEACHER_READ,
    "TEACHERS",
  ),
  createItem("Parents", "/parents", Users, PERMISSIONS.PARENT_READ, "PARENTS"),
  createItem(
    "Enrollments",
    "/enrollments",
    UserPlus,
    PERMISSIONS.ENROLLMENT_READ,
    "ENROLLMENTS",
  ),

  // --- FINANCE ---
  createHeader("Finance"),
  createItem(
    "Vote Heads",
    "/vote-heads",
    Wallet,
    PERMISSIONS.VOTE_HEAD_READ,
    "VOTE_HEADS",
  ),
  createItem(
    "Fee Structures",
    "/fee-structures",
    FileText,
    PERMISSIONS.FEE_STRUCTURE_READ,
    "FEE_STRUCTURES",
  ),
  createItem(
    "Fee Allocations",
    "/fee-allocations",
    PieChart,
    PERMISSIONS.FEE_ALLOCATION_READ,
    "FEE_ALLOCATIONS",
  ),
  createItem(
    "Bank Staging",
    "/bank-transactions-staging",
    Landmark,
    PERMISSIONS.BANK_STAGING_READ,
    "BANK_STAGING",
  ),
  createItem(
    "Fee Ledgers",
    "/student-fee-ledgers",
    CircleDollarSign,
    PERMISSIONS.STUDENT_LEDGER_READ,
    "STUDENT_FEE_LEDGERS",
  ),
  createItem(
    "Supplier Payments",
    "/supplier-payments",
    Banknote,
    PERMISSIONS.SUPPLIER_PAYMENT_READ,
    "SUPPLIER_PAYMENTS",
  ),
  createItem(
    "Payments",
    "/payments",
    Coins,
    PERMISSIONS.PAYMENT_READ,
    "PAYMENTS",
  ),

  // --- COMMUNICATIONS ---
  createHeader("Communications"),
  createItem(
    "Announcements",
    "/announcements",
    Megaphone,
    PERMISSIONS.ANNOUNCEMENT_READ,
    "ANNOUNCEMENTS",
  ),
  createItem(
    "Notifications",
    "/notifications",
    Bell,
    PERMISSIONS.NOTIFICATION_READ,
    "NOTIFICATIONS",
  ),
  createItem(
    "SMS Templates",
    "/sms-templates",
    MessageSquareText,
    PERMISSIONS.SMS_TEMPLATE_READ,
    "SMS_TEMPLATES",
  ),
  createItem(
    "SMS Outbox",
    "/sms-outbox",
    Send,
    PERMISSIONS.SMS_OUTBOX_READ,
    "SMS_OUTBOX",
  ),
  createItem(
    "Newsletters",
    "/newsletters",
    Newspaper,
    PERMISSIONS.NEWSLETTER_READ,
    "NEWSLETTERS",
  ),

  // --- INVENTORY ---
  createHeader("Inventory"),
  createItem(
    "Inventory",
    "/inventory",
    BoxIcon,
    PERMISSIONS.INVENTORY_READ,
    "INVENTORY",
  ),
  createItem(
    "Stock Levels",
    "/stock",
    BarChart2,
    PERMISSIONS.STOCK_READ,
    "STOCK_LEVELS",
  ),
  createItem(
    "Inventory Logs",
    "/inventory-logs",
    History,
    PERMISSIONS.INVENTORY_LOG_READ,
    "INVENTORY_LOGS",
  ),

  // --- PROCUREMENT ---
  createHeader("Procurement"),
  createItem(
    "Suppliers",
    "/suppliers",
    Truck,
    PERMISSIONS.SUPPLIER_READ,
    "SUPPLIERS",
  ),
  createItem(
    "Purchase Orders",
    "/purchase-orders",
    ShoppingCart,
    PERMISSIONS.PO_READ,
    "PURCHASE_ORDERS",
  ),

  // --- ADMINISTRATION ---
  createHeader("Administration"),
  createItem(
    "Branches",
    "/branches",
    Building,
    PERMISSIONS.BRANCH_READ,
    "BRANCHES",
  ),
  createItem("Users", "/users", UserCog, PERMISSIONS.USER_READ, "USERS"),
  createItem(
    "Roles & Perms",
    "/roles-permissions",
    ShieldCheck,
    PERMISSIONS.ROLE_READ,
    "ROLES",
  ),
  createItem(
    "Audit Logs",
    "/audit-logs",
    History,
    PERMISSIONS.AUDIT_LOG_READ,
    "AUDIT_LOGS",
  ),
];
